import { useRef, useState } from 'react';
import { AppHeader, ConfirmDialog, Toast } from '../components/common';
import { useT } from '../hooks/useT';
import { buildExport } from '../services/progress';
import { downloadJsonFile, readJsonFile } from '../services/storage';
import { useProgress } from '../state/ProgressContext';
import { useRouter } from '../state/RouterContext';
import { useSettings } from '../state/SettingsContext';
import { ttsController } from '../services/tts/TtsController';
import { useTts } from '../state/TtsContext';
import type { FontSize, LanguageMode, SpeechSpeed } from '../types';

/**
 * Where the Arabic actually comes from, and what to do about it.
 *
 * The app ships with its own recorded Arabic voice, so this is information
 * rather than a problem to solve — but a device that has a real Arabic voice
 * sounds better, so the steps to add one are here for anyone who wants them.
 */
function ArabicVoiceField() {
  const t = useT();
  const { uiLang } = useSettings();
  const { status } = useTts();
  const [downloading, setDownloading] = useState<number | null>(null);
  const [downloadResult, setDownloadResult] = useState<'done' | 'failed' | null>(null);
  const [showSteps, setShowSteps] = useState(false);

  const source = status.arabicSource;
  const stats = status.bundledStats;
  const megabytes = stats ? (stats.totalBytes / 1_048_576).toFixed(0) : '0';
  const minutes = stats ? Math.round(stats.totalMs / 60000) : 0;

  // Only meaningful once a service worker is in charge; without one there is
  // nowhere for the files to be kept.
  const canStoreOffline =
    typeof navigator !== 'undefined' && 'serviceWorker' in navigator && !!navigator.serviceWorker.controller;

  async function downloadEverything() {
    const urls = ttsController.bundled.allUrls();
    if (urls.length === 0) return;
    setDownloadResult(null);
    setDownloading(0);
    let failed = 0;
    for (let i = 0; i < urls.length; i += 1) {
      try {
        const res = await fetch(urls[i]);
        if (!res.ok) failed += 1;
      } catch {
        failed += 1;
      }
      setDownloading(Math.round(((i + 1) / urls.length) * 100));
    }
    setDownloading(null);
    setDownloadResult(failed === 0 ? 'done' : 'failed');
  }

  return (
    <div className="field">
      <span className="field__label">{t('voiceInUse')}</span>

      <div className={`notice ${source === 'none' ? 'notice--warn' : ''}`} role="status">
        <div className="notice__title">
          <span aria-hidden="true">{source === 'none' ? '🔇 ' : '🔊 '}</span>
          {source === 'bundled'
            ? t('voiceBundled')
            : source === 'device'
              ? t('voiceDevice')
              : t('noArabicVoiceTitle')}
        </div>
        <p style={{ margin: 0, fontSize: '0.85rem' }}>
          {source === 'bundled'
            ? t('voiceBundledNote')
            : source === 'device'
              ? t('voiceDeviceNote')
              : t('noArabicVoiceBody')}
        </p>
      </div>

      {source !== 'device' && status.bundledReady && stats ? (
        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          {uiLang === 'ar'
            ? `${stats.count} تسجيلاً · حوالي ${minutes} دقيقة · ${megabytes} ميغابايت`
            : `${stats.count} recordings · about ${minutes} minutes · ${megabytes} MB`}
        </p>
      ) : null}

      {canStoreOffline && status.bundledReady ? (
        <>
          <button
            type="button"
            className="btn btn--block"
            disabled={downloading !== null}
            onClick={() => void downloadEverything()}
          >
            <span className="btn__icon" aria-hidden="true">
              ⬇️
            </span>
            {downloading !== null ? `${t('downloadingAudio')} ${downloading}%` : t('downloadAudio')}
          </button>
          {downloadResult ? (
            <p
              style={{
                margin: 0,
                fontSize: '0.85rem',
                color: downloadResult === 'done' ? 'var(--success)' : 'var(--danger)',
              }}
            >
              {downloadResult === 'done' ? t('audioReadyOffline') : t('audioDownloadFailed')}
            </p>
          ) : null}
        </>
      ) : null}

      {!status.hasDeviceArabicVoice ? (
        <>
          <button
            type="button"
            className="btn btn--block"
            aria-expanded={showSteps}
            onClick={() => setShowSteps((v) => !v)}
          >
            <span className="btn__icon" aria-hidden="true">
              {showSteps ? '▾' : '▸'}
            </span>
            {t('installDeviceVoice')}
          </button>
          {showSteps ? (
            <div className="notice" style={{ fontSize: '0.85rem' }}>
              <p style={{ margin: 0 }}>{deviceVoiceSteps(uiLang)}</p>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** Free, official steps for whichever device this is. */
function deviceVoiceSteps(uiLang: 'ar' | 'en'): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isMac = /Macintosh/.test(ua) && !isIOS;

  if (isIOS) {
    return uiLang === 'ar'
      ? 'الإعدادات ← إمكانية الوصول ← المحتوى المنطوق ← الأصوات ← العربية، ثم نزّل صوتاً (مثل ماجد). بعدها أعد فتح التطبيق.'
      : 'Settings → Accessibility → Spoken Content → Voices → Arabic, then download a voice (Maged, for example). Reopen the app afterwards.';
  }
  if (isAndroid) {
    return uiLang === 'ar'
      ? 'الإعدادات ← النظام ← اللغات والإدخال ← إخراج تحويل النص إلى كلام ← تثبيت بيانات صوت العربية. بعدها أعد فتح التطبيق.'
      : 'Settings → System → Languages & input → Text-to-speech output → install the Arabic voice data. Reopen the app afterwards.';
  }
  if (isMac) {
    return uiLang === 'ar'
      ? 'إعدادات النظام ← إمكانية الوصول ← المحتوى المنطوق ← أصوات النظام ← إدارة الأصوات ← العربية. بعدها أعد فتح التطبيق.'
      : 'System Settings → Accessibility → Spoken Content → System Voice → Manage Voices → Arabic. Reopen the app afterwards.';
  }
  return uiLang === 'ar'
    ? 'الإعدادات ← الوقت واللغة ← اللغة والمنطقة ← إضافة لغة (العربية) ← خيارات اللغة ← تثبيت حزمة الكلام. بعدها أعد تشغيل المتصفح.'
    : 'Settings → Time & language → Language & region → add Arabic → Language options → install the Speech pack. Restart the browser afterwards.';
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card stack-sm">
      <h2 className="card__title">{title}</h2>
      {children}
    </section>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  const t = useT();
  return (
    <div className="switch-row">
      <span style={{ fontWeight: 700 }}>{label}</span>
      <button type="button" className="switch" aria-pressed={value} onClick={() => onChange(!value)}>
        {value ? `✅ ${t('yes')}` : `— ${t('no')}`}
      </button>
    </div>
  );
}

export function SettingsScreen() {
  const t = useT();
  const { settings, update, uiLang } = useSettings();
  const { progress, resetAll, importFrom } = useProgress();
  const { navigate } = useRouter();
  const { status, speak, speechLang, canSpeakText } = useTts();

  const testPhrase =
    speechLang === 'ar'
      ? 'مرحباً. هذه تجربة للصوت العربي. توقّف عند الإشارة الحمراء.'
      : 'Hello. This is a test of the English voice. Stop at the red sign.';

  const [confirmReset, setConfirmReset] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const languages: Array<{ value: LanguageMode; label: string }> = [
    { value: 'ar', label: t('langArabic') },
    { value: 'ar-en', label: t('langArabicEnglish') },
    { value: 'en', label: t('langEnglish') },
  ];

  const fontSizes: Array<{ value: FontSize; label: string }> = [
    { value: 'normal', label: t('fontNormal') },
    { value: 'large', label: t('fontLarge') },
    { value: 'xlarge', label: t('fontXLarge') },
  ];

  const speeds: Array<{ value: SpeechSpeed; label: string }> = [
    { value: 'slow', label: t('speedSlow') },
    { value: 'default', label: t('speedDefault') },
    { value: 'normal', label: t('speedNormal') },
  ];

  const handleImport = async (file: File) => {
    try {
      const raw = await readJsonFile(file);
      setToast(importFrom(raw) ? t('importDone') : t('importFailed'));
    } catch {
      setToast(t('importFailed'));
    }
  };

  return (
    <>
      <AppHeader title={t('settings')} />
      <main id="main" className="app-main stack">
        <Section title={t('displayMode')}>
          <div className="choice-row" role="group" aria-label={t('language')}>
            {languages.map((option) => (
              <button
                key={option.value}
                type="button"
                className="choice"
                aria-pressed={settings.languageMode === option.value}
                onClick={() => update({ languageMode: option.value })}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="switch-row">
            <span style={{ fontWeight: 700 }}>{t('learningStyle')}</span>
          </div>
          <div className="choice-row" role="group" aria-label={t('learningStyle')}>
            <button
              type="button"
              className="choice"
              aria-pressed={settings.easyMode}
              onClick={() => update({ easyMode: true })}
            >
              🌟 {t('easyMode')}
            </button>
            <button
              type="button"
              className="choice"
              aria-pressed={!settings.easyMode}
              onClick={() => update({ easyMode: false })}
            >
              ⚙️ {t('standardMode')}
            </button>
          </div>

          <div className="switch-row">
            <span style={{ fontWeight: 700 }}>{t('fontSize')}</span>
          </div>
          <div className="choice-row" role="group" aria-label={t('fontSize')}>
            {fontSizes.map((option) => (
              <button
                key={option.value}
                type="button"
                className="choice"
                aria-pressed={settings.fontSize === option.value}
                onClick={() => update({ fontSize: option.value })}
              >
                {option.label}
              </button>
            ))}
          </div>

          <Toggle
            label={t('highContrast')}
            value={settings.highContrast}
            onChange={(v) => update({ highContrast: v })}
          />
          <Toggle
            label={t('reducedMotion')}
            value={settings.reducedMotion}
            onChange={(v) => update({ reducedMotion: v })}
          />
        </Section>

        <Section title={`🔊 ${t('speechSpeed')}`}>
          <div className="choice-row" role="group" aria-label={t('speechSpeed')}>
            {speeds.map((option) => (
              <button
                key={option.value}
                type="button"
                className="choice"
                aria-pressed={settings.speechSpeed === option.value}
                onClick={() => update({ speechSpeed: option.value })}
              >
                {option.label}
              </button>
            ))}
          </div>

          <Toggle
            label={t('autoSpeak')}
            value={settings.autoSpeakQuestion}
            onChange={(v) => update({ autoSpeakQuestion: v })}
          />
          <Toggle
            label={t('speakFeedback')}
            value={settings.speakFeedback}
            onChange={(v) => update({ speakFeedback: v })}
          />

          <ArabicVoiceField />

          <div className="field">
            <label className="field__label" htmlFor="voice-ar">
              {t('voice')}
            </label>
            {status.arabicVoices.length === 0 ? (
              <div className="notice notice--warn">
                <div className="notice__title">{t('noArabicVoiceTitle')}</div>
                <p style={{ margin: 0, fontSize: '0.85rem' }}>{t('noArabicVoiceBody')}</p>
              </div>
            ) : (
              <select
                id="voice-ar"
                className="select"
                value={settings.arabicVoiceUri ?? ''}
                onChange={(e) => update({ arabicVoiceUri: e.target.value || null })}
              >
                <option value="">{t('voiceAutomatic')}</option>
                {status.arabicVoices.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name} ({voice.lang})
                  </option>
                ))}
              </select>
            )}
          </div>

          {status.englishVoices.length > 0 ? (
            <div className="field">
              <label className="field__label" htmlFor="voice-en">
                {t('englishVoice')}
              </label>
              <select
                id="voice-en"
                className="select"
                value={settings.englishVoiceUri ?? ''}
                onChange={(e) => update({ englishVoiceUri: e.target.value || null })}
              >
                <option value="">{uiLang === 'ar' ? 'تلقائي' : 'Automatic'}</option>
                {status.englishVoices.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name} ({voice.lang})
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <button
            type="button"
            className="btn btn--block"
            disabled={!canSpeakText(testPhrase, speechLang)}
            onClick={() => speak(testPhrase, speechLang, 'voice-test')}
          >
            <span className="btn__icon" aria-hidden="true">
              🔊
            </span>
            {t('testVoice')}
          </button>

          <div className="field">
            <label className="field__label" htmlFor="engine">
              {t('audioEngine')}
            </label>
            <select
              id="engine"
              className="select"
              value={settings.ttsEngine}
              onChange={(e) => update({ ttsEngine: e.target.value as 'web-speech' | 'piper' })}
            >
              <option value="web-speech">{t('engineBrowser')}</option>
              <option value="piper">{t('enginePiper')}</option>
            </select>
          </div>

          {settings.ttsEngine === 'piper' ? (
            <div className="field">
              <label className="field__label" htmlFor="piper-url">
                {t('piperUrl')}
              </label>
              <input
                id="piper-url"
                className="input ltr"
                type="text"
                value={settings.piperUrl}
                onChange={(e) => update({ piperUrl: e.target.value })}
                placeholder="http://localhost:5959"
              />
              {status.error ? (
                <div className="notice notice--danger" style={{ fontSize: '0.85rem' }}>
                  {status.error}
                </div>
              ) : null}
            </div>
          ) : null}
        </Section>

        <Section title={t('dataAndPrivacy')}>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t('privacyNote')}</p>

          <button
            type="button"
            className="btn btn--block"
            onClick={() => {
              downloadJsonFile(
                `ohio-permit-progress-${new Date().toISOString().slice(0, 10)}.json`,
                buildExport(progress),
              );
            }}
          >
            <span className="btn__icon" aria-hidden="true">
              💾
            </span>
            {t('exportProgress')}
          </button>

          <button type="button" className="btn btn--block" onClick={() => fileInput.current?.click()}>
            <span className="btn__icon" aria-hidden="true">
              📂
            </span>
            {t('importProgress')}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            className="visually-hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleImport(file);
              e.target.value = '';
            }}
          />

          <button type="button" className="btn btn--danger btn--block" onClick={() => setConfirmReset(true)}>
            <span className="btn__icon" aria-hidden="true">
              🗑
            </span>
            {t('resetProgress')}
          </button>
        </Section>

        <Section title={t('more')}>
          <button type="button" className="btn btn--block" onClick={() => navigate({ name: 'caregiver' })}>
            <span className="btn__icon" aria-hidden="true">
              👨‍👩‍👦
            </span>
            {t('caregiver')}
          </button>
          <button type="button" className="btn btn--block" onClick={() => navigate({ name: 'manager' })}>
            <span className="btn__icon" aria-hidden="true">
              ✏️
            </span>
            {t('questionManager')}
          </button>
          <button type="button" className="btn btn--block" onClick={() => navigate({ name: 'help' })}>
            <span className="btn__icon" aria-hidden="true">
              ℹ️
            </span>
            {t('keyboardHelp')}
          </button>
        </Section>
      </main>

      {confirmReset ? (
        <ConfirmDialog
          message={t('resetConfirm')}
          confirmLabel={t('resetProgress')}
          onCancel={() => setConfirmReset(false)}
          onConfirm={() => {
            resetAll();
            setConfirmReset(false);
            setToast(t('resetDone'));
          }}
        />
      ) : null}

      {toast ? <Toast message={toast} onDone={() => setToast(null)} /> : null}
    </>
  );
}
